/**
 * HM-511 cluster 24 Modulo 7 Room Service admin — Kanban ordini.
 *
 * SPEC v2.6 Modulo 7: CRUD menù + Kanban live con drag&drop tra colonne
 * pending → preparing → delivered. Per ora UI Kanban (drag-and-drop via
 * @dnd-kit) + pulsanti "Avanza" come fallback accessibile. CRUD menù
 * (catalogo) rinviato perche' serve nuova tabella (menu attualmente
 * hardcoded in apps/api-local/src/data/room-service-menu.ts).
 */
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@hotel-manager/ui/dnd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  type RsOrder,
  type RsOrderStatus,
  listRsOrders,
  patchRsOrderStatus,
} from '../../api/room-service-admin.js';
import { fetchRooms } from '../../api/rooms.js';

const COLUMNS: Array<{ id: RsOrderStatus; label: string; color: string }> = [
  { id: 'pending', label: 'In attesa', color: '#FFF3E6' },
  { id: 'preparing', label: 'In preparazione', color: '#DCE7F0' },
  { id: 'delivered', label: 'Consegnati', color: '#E4EDDE' },
];

const TRANSITION_MAP: Record<RsOrderStatus, RsOrderStatus | null> = {
  pending: 'preparing',
  preparing: 'delivered',
  delivered: null,
  cancelled: null,
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function RoomServiceAdminPage() {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const ordersQuery = useQuery({
    queryKey: ['rs-orders', 'kanban'],
    queryFn: () => listRsOrders(),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });

  const roomsQuery = useQuery({
    queryKey: ['rooms', 'all'],
    queryFn: fetchRooms,
    staleTime: 5 * 60_000,
  });

  const patchMut = useMutation({
    mutationFn: ({ id, next }: { id: string; next: RsOrderStatus }) => patchRsOrderStatus(id, next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rs-orders'] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
      setError(null);
    },
    onError: (err: Error) => setError(err.message ?? 'Errore aggiornamento ordine'),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const orders: RsOrder[] = ordersQuery.data?.orders ?? [];

  const roomCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of roomsQuery.data?.rooms ?? []) m.set(r.id, r.code);
    return m;
  }, [roomsQuery.data]);

  const byColumn = useMemo(() => {
    const m: Record<RsOrderStatus, RsOrder[]> = {
      pending: [],
      preparing: [],
      delivered: [],
      cancelled: [],
    };
    for (const o of orders) {
      m[o.status]?.push(o);
    }
    return m;
  }, [orders]);

  function handleDragEnd(e: DragEndEvent) {
    const orderId = String(e.active.id);
    const target = e.over?.id ? String(e.over.id) : null;
    if (!target) return;
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    if (target === order.status) return;
    // Solo transitions forward (pending → preparing → delivered).
    const forward = TRANSITION_MAP[order.status];
    if (target !== forward) {
      setError(`Transizione non consentita: ${order.status} → ${target}`);
      return;
    }
    patchMut.mutate({ id: orderId, next: target as RsOrderStatus });
  }

  return (
    <div className="px-6 py-6" data-testid="rs-admin-page">
      <h1 style={{ fontSize: 28, fontWeight: 700, color: '#3D2F24', margin: 0 }}>
        Room Service · Kanban
      </h1>
      <p style={{ fontSize: 13, color: '#7A6957', margin: '4px 0 16px 0' }}>
        Drag&apos;n&apos;drop ordini fra colonne, oppure clicca &laquo;Avanza&raquo; · auto-refresh
        30s
      </p>

      {error && (
        <div
          data-testid="rs-admin-error"
          style={{
            padding: 10,
            background: '#FFE5DC',
            color: '#A8453A',
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
          }}
        >
          {COLUMNS.map((col) => (
            <KanbanColumn
              key={col.id}
              columnId={col.id}
              label={col.label}
              color={col.color}
              orders={byColumn[col.id] ?? []}
              roomCodeById={roomCodeById}
              onAdvance={(orderId, next) => patchMut.mutate({ id: orderId, next })}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
}

function KanbanColumn({
  columnId,
  label,
  color,
  orders,
  roomCodeById,
  onAdvance,
}: {
  columnId: RsOrderStatus;
  label: string;
  color: string;
  orders: RsOrder[];
  roomCodeById: Map<string, string>;
  onAdvance: (id: string, next: RsOrderStatus) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId });
  return (
    <div
      ref={setNodeRef}
      data-testid={`rs-col-${columnId}`}
      style={{
        background: color,
        border: '1px solid #E8DECF',
        borderRadius: 12,
        padding: 14,
        minHeight: 400,
        outline: isOver ? '2px dashed #E97B3D' : 'none',
        transition: 'outline 0.1s',
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#3D2F24',
          marginBottom: 12,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>{label}</span>
        <span style={{ color: '#7A6957' }}>{orders.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {orders.length === 0 && (
          <div style={{ fontSize: 12, color: '#7A6957', textAlign: 'center', padding: '20px 0' }}>
            Nessun ordine
          </div>
        )}
        {orders.map((o) => (
          <KanbanCard
            key={o.id}
            order={o}
            roomCode={roomCodeById.get(o.roomId) ?? '?'}
            onAdvance={onAdvance}
          />
        ))}
      </div>
    </div>
  );
}

function KanbanCard({
  order,
  roomCode,
  onAdvance,
}: {
  order: RsOrder;
  roomCode: string;
  onAdvance: (id: string, next: RsOrderStatus) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.id,
  });
  const next = TRANSITION_MAP[order.status];

  const style: React.CSSProperties = {
    background: '#FFF',
    border: '1px solid #E8DECF',
    borderRadius: 8,
    padding: 10,
    fontSize: 13,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.6 : 1,
    cursor: 'grab',
    boxShadow: '0 1px 3px rgba(45, 36, 29, 0.08)',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`rs-card-${order.id}`}
      {...listeners}
      {...attributes}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Camera {roomCode}</strong>
        <span style={{ fontSize: 11, color: '#7A6957' }}>{formatTime(order.placedAt)}</span>
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: '#7A6957' }}>
        € {Number.parseFloat(order.total).toFixed(2)}
        {order.note ? ` · ${order.note.slice(0, 30)}` : ''}
      </div>
      {next && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAdvance(order.id, next);
          }}
          data-testid={`rs-advance-${order.id}`}
          style={{
            marginTop: 6,
            padding: '4px 10px',
            background: '#E97B3D',
            color: '#FFF',
            border: 'none',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Avanza → {next === 'preparing' ? 'In preparazione' : 'Consegnato'}
        </button>
      )}
    </div>
  );
}
